// kdTree.js

class Node {
  constructor(obj, dimension, parent) {
    this.obj = obj;
    this.left = null;
    this.right = null;
    this.parent = parent;
    this.dimension = dimension;
  }
}

export class Coordinate {
  constructor(latitude, longitude) {
    this._latitude = latitude;
    this._longitude = longitude;
  }
  get latitude() {
    return this._latitude;
  }
  set latitude(value) {
    this._latitude = value;
  }
  get longitude() {
    return this._longitude;
  }
  set longitude(value) {
    this._longitude = value;
  }
  getStrCoordinate() {
    return this._latitude + ',' + this._longitude;
  }
  getArrCoordinate() {
    return [this._latitude, this._longitude];
  }
  getObjCoordinate() {
    return { latitude: this._latitude, longitude: this._longitude };
  }
  static fromArray(arr) {
    return new Coordinate(arr[0], arr[1]);
  }
  static fromString(str) {
    const arr = str.split(',');
    return new Coordinate(arr[0], arr[1]);
  }
  static fromObject(obj) {
    return new Coordinate(obj.latitude, obj.longitude);
  }
  static isEqualFromArray(a, b) {
    return a[0] === b[0] && a[1] === b[1];
  }
}
export function haversineDistance(point1, point2) {
  const R = 6371; // Radius of the Earth in kilometers
  const lat1 = (point1.latitude * Math.PI) / 180;
  const lon1 = (point1.longitude * Math.PI) / 180;
  const lat2 = (point2.latitude * Math.PI) / 180;
  const lon2 = (point2.longitude * Math.PI) / 180;

  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export class kdTree {
  constructor(points, metric, dimensions) {
    this.dimensions = dimensions;
    this.metric = metric;

    if (!Array.isArray(points)) {
      this.loadTree(points);
    } else {
      this.root = this.buildTree(points, 0, null);
    }
  }

  buildTree(points, depth, parent) {
    const dim = depth % this.dimensions.length;
    let median, node;

    if (points.length === 0) {
      return null;
    }
    if (points.length === 1) {
      return new Node(points[0], dim, parent);
    }

    points.sort((a, b) => a[this.dimensions[dim]] - b[this.dimensions[dim]]);
    median = Math.floor(points.length / 2);
    node = new Node(points[median], dim, parent);
    node.left = this.buildTree(points.slice(0, median), depth + 1, node);
    node.right = this.buildTree(points.slice(median + 1), depth + 1, node);

    return node;
  }

  loadTree(data) {
    this.root = data;
    const restoreParent = (root) => {
      if (root.left) {
        root.left.parent = root;
        restoreParent(root.left);
      }
      if (root.right) {
        root.right.parent = root;
        restoreParent(root.right);
      }
    };
    restoreParent(this.root);
  }

  toJSON(src) {
    if (!src) src = this.root;
    const dest = new Node(src.obj, src.dimension, null);
    if (src.left) dest.left = this.toJSON(src.left);
    if (src.right) dest.right = this.toJSON(src.right);
    return dest;
  }

  insert(point) {
    const innerSearch = (node, parent) => {
      if (node === null) {
        return parent;
      }

      const dimension = this.dimensions[node.dimension];
      if (point[dimension] < node.obj[dimension]) {
        return innerSearch(node.left, node);
      } else {
        return innerSearch(node.right, node);
      }
    };

    const insertPosition = innerSearch(this.root, null);
    const newNode = new Node(
      point,
      (insertPosition.dimension + 1) % this.dimensions.length,
      insertPosition,
    );
    const dimension = this.dimensions[insertPosition.dimension];

    if (insertPosition === null) {
      this.root = new Node(point, 0, null);
      return;
    }

    if (point[dimension] < insertPosition.obj[dimension]) {
      insertPosition.left = newNode;
    } else {
      insertPosition.right = newNode;
    }
  }

  remove(point) {
    const nodeSearch = (node) => {
      if (node === null) {
        return null;
      }
      if (node.obj === point) {
        return node;
      }
      const dimension = this.dimensions[node.dimension];
      if (point[dimension] < node.obj[dimension]) {
        return nodeSearch(node.left, node);
      } else {
        return nodeSearch(node.right, node);
      }
    };

    const removeNode = (node) => {
      const findMin = (node, dim) => {
        if (node === null) {
          return null;
        }
        const dimension = this.dimensions[dim];
        if (node.dimension === dim) {
          if (node.left !== null) {
            return findMin(node.left, dim);
          }
          return node;
        }
        const left = findMin(node.left, dim);
        const right = findMin(node.right, dim);
        let min = node;
        if (left !== null && left.obj[dimension] < min.obj[dimension]) {
          min = left;
        }
        if (right !== null && right.obj[dimension] < min.obj[dimension]) {
          min = right;
        }
        return min;
      };

      if (node.left === null && node.right === null) {
        if (node.parent === null) {
          this.root = null;
          return;
        }
        const pDimension = this.dimensions[node.parent.dimension];
        if (node.obj[pDimension] < node.parent.obj[pDimension]) {
          node.parent.left = null;
        } else {
          node.parent.right = null;
        }
        return;
      }

      let nextNode, nextObj;
      if (node.right !== null) {
        nextNode = findMin(node.right, node.dimension);
        nextObj = nextNode.obj;
        removeNode(nextNode);
        node.obj = nextObj;
      } else {
        nextNode = findMin(node.left, node.dimension);
        nextObj = nextNode.obj;
        removeNode(nextNode);
        node.right = node.left;
        node.left = null;
        node.obj = nextObj;
      }
    };

    const node = nodeSearch(this.root);
    if (node !== null) {
      removeNode(node);
    }
  }

  nearest(point, maxNodes, maxDistance) {
    const bestNodes = new BinaryHeap((e) => -e[1]);

    const nearestSearch = (node) => {
      const dimension = this.dimensions[node.dimension];
      const ownDistance = this.metric(point, node.obj);
      const linearPoint = {};
      for (let i = 0; i < this.dimensions.length; i += 1) {
        if (i === node.dimension) {
          linearPoint[this.dimensions[i]] = point[this.dimensions[i]];
        } else {
          linearPoint[this.dimensions[i]] = node.obj[this.dimensions[i]];
        }
      }
      const linearDistance = this.metric(linearPoint, node.obj);
      const saveNode = (node, distance) => {
        bestNodes.push([node, distance]);
        if (bestNodes.size() > maxNodes) {
          bestNodes.pop();
        }
      };

      if (node.right === null && node.left === null) {
        if (bestNodes.size() < maxNodes || ownDistance < bestNodes.peek()[1]) {
          saveNode(node, ownDistance);
        }
        return;
      }

      let bestChild;
      if (node.right === null) {
        bestChild = node.left;
      } else if (node.left === null) {
        bestChild = node.right;
      } else {
        if (point[dimension] < node.obj[dimension]) {
          bestChild = node.left;
        } else {
          bestChild = node.right;
        }
      }

      nearestSearch(bestChild);
      if (bestNodes.size() < maxNodes || ownDistance < bestNodes.peek()[1]) {
        saveNode(node, ownDistance);
      }

      if (
        bestNodes.size() < maxNodes ||
        Math.abs(linearDistance) < bestNodes.peek()[1]
      ) {
        const otherChild = bestChild === node.left ? node.right : node.left;
        if (otherChild !== null) {
          nearestSearch(otherChild);
        }
      }
    };

    if (maxDistance) {
      for (let i = 0; i < maxNodes; i += 1) {
        bestNodes.push([null, maxDistance]);
      }
    }

    if (this.root) nearestSearch(this.root);

    const result = [];
    for (let i = 0; i < Math.min(maxNodes, bestNodes.content.length); i += 1) {
      if (bestNodes.content[i][0]) {
        result.push([bestNodes.content[i][0].obj, bestNodes.content[i][1]]);
      }
    }
    return result;
  }

  balanceFactor() {
    const height = (node) => {
      if (node === null) {
        return 0;
      }
      return Math.max(height(node.left), height(node.right)) + 1;
    };

    const count = (node) => {
      if (node === null) {
        return 0;
      }
      return count(node.left) + count(node.right) + 1;
    };

    return height(this.root) / (Math.log(count(this.root)) / Math.log(2));
  }
}

class BinaryHeap {
  constructor(scoreFunction) {
    this.content = [];
    this.scoreFunction = scoreFunction;
  }

  push(element) {
    this.content.push(element);
    this.bubbleUp(this.content.length - 1);
  }

  pop() {
    const result = this.content[0];
    const end = this.content.pop();
    if (this.content.length > 0) {
      this.content[0] = end;
      this.sinkDown(0);
    }
    return result;
  }

  peek() {
    return this.content[0];
  }

  remove(node) {
    const len = this.content.length;
    for (let i = 0; i < len; i++) {
      if (this.content[i] === node) {
        const end = this.content.pop();
        if (i !== len - 1) {
          this.content[i] = end;
          if (this.scoreFunction(end) < this.scoreFunction(node))
            this.bubbleUp(i);
          else this.sinkDown(i);
        }
        return;
      }
    }
    throw new Error('Node not found.');
  }

  size() {
    return this.content.length;
  }

  bubbleUp(n) {
    const element = this.content[n];
    while (n > 0) {
      const parentN = Math.floor((n + 1) / 2) - 1;
      const parent = this.content[parentN];
      if (this.scoreFunction(element) < this.scoreFunction(parent)) {
        this.content[parentN] = element;
        this.content[n] = parent;
        n = parentN;
      } else {
        break;
      }
    }
  }

  sinkDown(n) {
    const length = this.content.length;
    const element = this.content[n];
    const elemScore = this.scoreFunction(element);
    while (true) {
      const child2N = (n + 1) * 2;
      const child1N = child2N - 1;
      let swap = null;

      if (child1N < length) {
        const child1 = this.content[child1N];
        const child1Score = this.scoreFunction(child1);
        if (child1Score < elemScore) swap = child1N;
      }

      if (child2N < length) {
        const child2 = this.content[child2N];
        const child2Score = this.scoreFunction(child2);
        if (
          child2Score <
          (swap === null
            ? elemScore
            : this.scoreFunction(this.content[child1N]))
        ) {
          swap = child2N;
        }
      }

      if (swap !== null) {
        this.content[n] = this.content[swap];
        this.content[swap] = element;
        n = swap;
      } else {
        break;
      }
    }
  }
}
